import { translate } from '@utils/i18n';
import { formatFileSize } from '@features/Attachments/utils/fileFormatting';
import { fileAccessLabel, FILE_ACCESS_VALUES } from './fileAccess';
import { ATTR_FILE_ACCESS, FILE_ACCESS_PUBLIC, INVOICE_ATTACHMENTS_NS, UPLOAD_NS } from '../constants';
/** Drawer column key — the `fields` key the queue rows read their value from. */
const COLUMN_FILE_ACCESS = 'fileAccess';
/**
 * Module-level constant, not an inline literal: it feeds `useDirectUpload`'s `addFiles`
 * dependency, so a fresh object per render would rebuild that callback every time.
 *
 * Defaults to `public`, matching the column default (`file_access integer default 0`) and the
 * legacy form, whose radio group lands on the first enum value. A new attachment is normally one
 * the customer is meant to receive.
 */
export const INVOICE_DEFAULT_UPLOAD_ATTRIBUTES = {
    [ATTR_FILE_ACCESS]: FILE_ACCESS_PUBLIC
};
/** Projects a staged row's finalize attributes onto the drawer columns' display values. */
export function toInvoiceQueueFields(item) {
    var _a;
    return {
        [COLUMN_FILE_ACCESS]: (_a = item.attributes[ATTR_FILE_ACCESS]) !== null && _a !== void 0 ? _a : FILE_ACCESS_PUBLIC
    };
}
/** All `AddAttachmentsBrushaway` copy — the organism takes `labels` and never imports `translate`. */
export function buildAddAttachmentsLabels(maxSizeBytes) {
    const sizeLimitLabel = formatFileSize(maxSizeBytes);
    const NS = UPLOAD_NS;
    return {
        title: translate('title', { namespace: NS }),
        columnName: translate('column_name', { namespace: NS }),
        columnType: translate('column_type', { namespace: NS }),
        retryActionLabel: translate('retry_action', { namespace: NS }),
        removeActionLabel: (fileName) => translate('remove_action', { namespace: NS, fileName }),
        metaSize: (size) => translate('meta.size', { namespace: NS, size }),
        metaWaiting: (size) => translate('meta.waiting', { namespace: NS, size }),
        metaUploaded: (size) => translate('meta.uploaded', { namespace: NS, size }),
        metaFinalizing: (size) => translate('meta.finalizing', { namespace: NS, size }),
        footerTotals: (count, size) => translate('footer_totals', { namespace: NS, count, size }),
        cancelActionLabel: translate('cancel_action', { namespace: NS }),
        metaFailed: (size) => translate('meta.failed', { namespace: NS, size }),
        addButtonLabel: (count) => translate('add_button', { namespace: NS, count }),
        uploadingSummary: (done, total, pct) => translate('uploading_summary', { namespace: NS, done, total, pct }),
        uploadedSummary: (done, total, failed) => translate('uploaded_summary', { namespace: NS, done, total, failed }),
        cancelUploadLabel: translate('cancel_upload', { namespace: NS }),
        retryFailedLabel: translate('retry_failed', { namespace: NS }),
        rejectedAlertTitle: (count) => translate('rejected_alert.title', { namespace: NS, count }),
        rejectedAlertMessage: (fileName, size) => translate('rejected_alert.message', { namespace: NS, fileName, size, limit: sizeLimitLabel }),
        rejectedAlertTypeMessage: (fileName) => translate('rejected_alert.type_message', { namespace: NS, fileName }),
        dropzoneNote: translate('dropzone_note', { namespace: NS, limit: sizeLimitLabel }),
        removeConfirmTitle: translate('remove_confirm.title', { namespace: NS }),
        removeConfirmDescription: (fileName) => translate('remove_confirm.description', { namespace: NS, fileName }),
        removeConfirmConfirmLabel: translate('remove_confirm.confirm_label', { namespace: NS }),
        removeConfirmCancelLabel: translate('remove_confirm.cancel_label', { namespace: NS }),
        discardConfirmTitle: translate('discard_confirm.title', { namespace: NS }),
        discardConfirmDescription: translate('discard_confirm.description', { namespace: NS }),
        discardConfirmConfirmLabel: translate('discard_confirm.confirm_label', { namespace: NS }),
        discardConfirmCancelLabel: translate('discard_confirm.cancel_label', { namespace: NS }),
        closeLabel: translate('close_label', { namespace: NS })
    };
}
const tableT = (key) => translate(key, { namespace: INVOICE_ATTACHMENTS_NS });
/**
 * Invoice's single drawer column — Visibility. A `select`, not Work Order's `flags`: `file_access`
 * is one value out of two, so a checkbox would imply the two can be combined.
 */
export function buildAddAttachmentsColumns(updateItemMetadata) {
    return [
        {
            kind: 'select',
            key: COLUMN_FILE_ACCESS,
            label: tableT('columns.visibility'),
            width: '170px',
            options: FILE_ACCESS_VALUES.map((value) => ({ value, label: fileAccessLabel(value, tableT) })),
            onChange: (id, value) => updateItemMetadata(id, { attributes: { [ATTR_FILE_ACCESS]: value } })
        }
    ];
}
